import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { FiX, FiSend, FiUsers, FiPercent, FiMessageCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { formatarData } from '../utils/dateUtils';

const PromocaoModal = ({ viagem, onClose }) => {
  const [passageiros, setPassageiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [desconto, setDesconto] = useState('');
  const [mensagemPromocao, setMensagemPromocao] = useState('');
  const [pesquisaPassageiro, setPesquisaPassageiro] = useState('');

  const passageirosFiltrados = useMemo(() => {
    const termo = pesquisaPassageiro.trim().toLowerCase();
    if (!termo) return passageiros;

    const termoNumeros = termo.replace(/\D/g, '');

    return passageiros.filter((p) => {
      const nome = (p.nome || '').toLowerCase();
      const cidade = (p.cidade || '').toLowerCase();
      const telefone = (p.telefone || '').toLowerCase();
      const telefoneNumeros = (p.telefone || '').replace(/\D/g, '');

      return (
        nome.includes(termo) ||
        cidade.includes(termo) ||
        telefone.includes(termo) ||
        (termoNumeros.length > 0 && telefoneNumeros.includes(termoNumeros))
      );
    });
  }, [passageiros, pesquisaPassageiro]);

  useEffect(() => {
    fetchPassageiros();
    gerarMensagemPadrao();
  }, [viagem, desconto]);

  const fetchPassageiros = async () => {
    try {
      const response = await api.get('/api/passageiros/primeiros-10');
      setPassageiros(response.data);
    } catch (error) {
      toast.error('Erro ao carregar passageiros');
    } finally {
      setLoading(false);
    }
  };

  const gerarMensagemPadrao = () => {
    if (!viagem) return;
    
    const valorOriginal = parseFloat(viagem.valor || 0);
    const descontoNum = parseFloat(desconto) || 0;
    const valorComDesconto = descontoNum > 0 
      ? valorOriginal * (1 - descontoNum / 100)
      : valorOriginal;

    const mensagem = `🎉 *PROMOÇÃO EXCLUSIVA - ${viagem.titulo.toUpperCase()}* 🎉

Olá! Você foi selecionado(a) para uma promoção especial!

📌 *Detalhes da Viagem:*
• Destino: ${viagem.destino}
• Data de Ida: ${formatarData(viagem.data_ida)}
${viagem.data_volta ? `• Data de Volta: ${formatarData(viagem.data_volta)}` : ''}
• Valor Original: R$ ${valorOriginal.toFixed(2)}

${descontoNum > 0 ? `💰 *DESCONTO ESPECIAL: ${descontoNum}% OFF*
• Valor com Desconto: R$ ${valorComDesconto.toFixed(2)}
• Você economiza: R$ ${(valorOriginal - valorComDesconto).toFixed(2)}` : ''}

${viagem.local_embarque ? `📍 Embarque: ${viagem.local_embarque}` : ''}
${viagem.horario_embarque ? `⏰ Horário: ${viagem.horario_embarque}` : ''}

🎁 *Esta promoção é válida apenas para os 10 primeiros clientes!*

Para garantir sua vaga com desconto, entre em contato conosco o quanto antes!

📞 Responda esta mensagem ou entre em contato através dos nossos canais.

_NetalimaTurismo - Sua viagem dos sonhos!_`;

    setMensagemPromocao(mensagem);
  };

  const formatarTelefone = (telefone) => {
    if (!telefone) return '';
    // Remove tudo que não é número
    const numeros = telefone.replace(/\D/g, '');
    // Se começar com 0, remove
    const semZero = numeros.startsWith('0') ? numeros.substring(1) : numeros;
    return semZero;
  };

  const abrirWhatsApp = (passageiro) => {
    const telefone = formatarTelefone(passageiro.telefone);
    if (!telefone) {
      toast.error(`Telefone inválido para ${passageiro.nome}`);
      return;
    }

    const mensagemEncoded = encodeURIComponent(mensagemPromocao);
    const url = `https://wa.me/55${telefone}?text=${mensagemEncoded}`;
    window.open(url, '_blank');
  };

  const enviarTodos = async () => {
    if (passageiros.length === 0) {
      toast.error('Nenhum passageiro selecionado');
      return;
    }

    if (!desconto || parseFloat(desconto) <= 0) {
      if (!window.confirm('Você não definiu um desconto. Deseja continuar mesmo assim?')) {
        return;
      }
    }

    setEnviando(true);
    
    try {
      // Marcar passageiros como receberam promoção no banco
      const passageiroIds = passageiros.map(p => p.id);
      await api.post('/api/passageiros/marcar-promocao-recebida', {
        passageiro_ids: passageiroIds,
        viagem_id: viagem.id
      });
      
      // Abrir WhatsApp para cada passageiro
      passageiros.forEach((passageiro, index) => {
        setTimeout(() => {
          abrirWhatsApp(passageiro);
        }, index * 2000); // Delay de 2 segundos entre cada abertura
      });

      setTimeout(() => {
        setEnviando(false);
        toast.success(`Promoção enviada para ${passageiros.length} passageiros`);
      }, passageiros.length * 2000);
    } catch (error) {
      setEnviando(false);
      toast.error('Erro ao marcar promoção enviada');
      console.error(error);
    }
  };

  const copiarMensagem = () => {
    navigator.clipboard.writeText(mensagemPromocao);
    toast.success('Mensagem copiada para a área de transferência!');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-sky-50 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-600 to-sky-500 text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <FiMessageCircle size={24} />
              Enviar Promoção via WhatsApp
            </h2>
            <p className="text-sky-100 mt-1">{viagem?.titulo}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Configuração de Desconto */}
          <div className="bg-white rounded-lg p-6 border border-sky-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiPercent size={20} />
              Configurar Desconto
            </h3>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Percentual de Desconto (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={desconto}
                  onChange={(e) => setDesconto(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                  placeholder="Ex: 10"
                />
              </div>
              {desconto && parseFloat(desconto) > 0 && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">Valor Original</p>
                  <p className="text-xl font-bold text-gray-900">
                    R$ {parseFloat(viagem?.valor || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-green-600 font-semibold mt-1">
                    Com {desconto}% OFF
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    R$ {(parseFloat(viagem?.valor || 0) * (1 - parseFloat(desconto) / 100)).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Lista de Passageiros */}
          <div className="bg-white rounded-lg p-6 border border-sky-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiUsers size={20} />
              10 Primeiros Clientes Cadastrados
            </h3>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
              </div>
            ) : passageiros.length === 0 ? (
              <p className="text-gray-600 text-center py-8">
                Nenhum passageiro encontrado com telefone cadastrado.
              </p>
            ) : (
              <>
                <div className="mb-4">
                  <input
                    type="search"
                    value={pesquisaPassageiro}
                    onChange={(e) => setPesquisaPassageiro(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    placeholder="Pesquisar por nome, telefone ou cidade..."
                  />
                </div>

                {passageirosFiltrados.length === 0 ? (
                  <p className="text-gray-600 text-center py-6">Nenhum passageiro encontrado.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {passageirosFiltrados.map((passageiro, index) => (
                      <div
                        key={passageiro.id}
                        className="flex items-center justify-between p-3 bg-sky-50 rounded-lg border border-sky-100"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-sky-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{passageiro.nome}</p>
                            <p className="text-sm text-gray-600">
                              {passageiro.telefone} {passageiro.cidade ? `• ${passageiro.cidade}` : ''}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => abrirWhatsApp(passageiro)}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2"
                        >
                          <FiSend size={16} />
                          Enviar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Preview da Mensagem */}
          <div className="bg-white rounded-lg p-6 border border-sky-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Preview da Mensagem</h3>
              <button
                onClick={copiarMensagem}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors text-sm"
              >
                Copiar Mensagem
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                {mensagemPromocao}
              </pre>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 border-t border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {passageiros.length} passageiro(s) selecionado(s)
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
            >
              Fechar
            </button>
            <button
              onClick={enviarTodos}
              disabled={enviando || passageiros.length === 0}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FiSend size={18} />
              {enviando ? 'Enviando...' : 'Enviar para Todos'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromocaoModal;
